import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = async () => {
      try {
        const authenticatedUser = await authService.checkAuth();
        setUser(authenticatedUser);
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Subscribe to auth state changes
    const unsubscribe = authService.subscribe((newUser) => {
      setUser(newUser);
    });

    return () => unsubscribe();
  }, []);

  const login = (userData) => {
    // Note: Actual login is handled by OAuth redirect
    // This is mainly for updating local state after login
    setUser(userData);
  };

  const logout = async () => {
    setLoading(true);
    await authService.logout();
    setUser(null);
    setLoading(false);
  };

  const updateUser = (updatedData) => {
    const newUserData = { ...user, ...updatedData };
    setUser(newUserData);
  };

  /**
   * Refresh user data from the API
   * Useful after organization changes (e.g., being added to a new org)
   */
  const refreshUser = useCallback(async () => {
    try {
      const authenticatedUser = await authService.checkAuth();
      setUser(authenticatedUser);
      return authenticatedUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  // Extract organizations from user object
  // New /auth/me response includes organizations array
  const organizations = useMemo(() => {
    return user?.organizations || [];
  }, [user]);

  // Computed properties for user organization state
  const hasOrganizations = organizations.length > 0;
  const isSingleOrg = organizations.length === 1;
  const isMultiOrg = organizations.length > 1;
  const hasNoOrg = user && organizations.length === 0;

  const value = {
    // User data
    user,
    login,
    logout,
    updateUser,
    refreshUser,
    loading,
    isAuthenticated: !!user,

    // Organization-related computed values
    organizations,        // Array of { id, name, slug, role }
    hasOrganizations,     // true if user has at least one org
    isSingleOrg,          // true if user has exactly one org
    isMultiOrg,           // true if user has multiple orgs
    hasNoOrg,             // true if authenticated but no org membership
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
